import React, { useState, useEffect } from 'react';
import { 
  Plus, Edit, Trash2, Save, X, Eye, ChevronUp, ChevronDown, GripVertical,
  FileText, Settings, Check, AlertCircle, CheckCircle, Copy, Play,
  ArrowUp, ArrowDown, RotateCcw, Wand2, Users, BarChart3
} from 'lucide-react';

const RiskProfileCreatePage = ({ onClose, editingForm = null }) => {
  // API base URL
  const API_BASE_URL = `${process.env.REACT_APP_API_BASE_URL}/api`;

  // Form state
  const [formData, setFormData] = useState({
    formName: '',
    description: '',
    isActive: true,
    isDefault: false
  });

  // Questions state
  const [questions, setQuestions] = useState([]);
  const [draggedQuestionIndex, setDraggedQuestionIndex] = useState(null);

  // UI state
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [showPreview, setShowPreview] = useState(false);
  const [expandedQuestion, setExpandedQuestion] = useState(null);
  const [activeTab, setActiveTab] = useState('builder'); // builder, preview, settings

  // Initialize form data if editing
  useEffect(() => {
    if (editingForm) {
      loadFormData(editingForm.id);
    } else {
      // Initialize with a sample question for new forms
      setQuestions([createNewQuestion(1)]);
    }
  }, [editingForm]);

  // Load existing form data
  const loadFormData = async (formId) => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/questionnaire-forms/${formId}`);
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      
      const data = await response.json();
      if (data.success) {
        const form = data.data;
        setFormData({
          formName: form.formName,
          description: form.description || '',
          isActive: form.isActive,
          isDefault: form.isDefault
        });
        setQuestions(form.questions || []);
      }
    } catch (error) {
      console.error('Error loading form:', error);
      showMessage('Failed to load form data', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Create new question template
  const createNewQuestion = (order) => ({
    id: null,
    questionOrder: order,
    questionText: '',
    questionType: 'SINGLE_CHOICE',
    options: [
      { id: null, optionOrder: 1, optionText: '', optionScore: 1 },
      { id: null, optionOrder: 2, optionText: '', optionScore: 2 }
    ]
  });

  // Add new question
  const addQuestion = () => {
    const newQuestion = createNewQuestion(questions.length + 1);
    setQuestions([...questions, newQuestion]);
    setExpandedQuestion(questions.length);
  };

  // Remove question
  const removeQuestion = (index) => {
    if (questions.length <= 1) {
      showMessage('Form must have at least one question', 'error');
      return;
    }
    
    const updatedQuestions = questions.filter((_, i) => i !== index);
    // Reorder remaining questions
    const reorderedQuestions = updatedQuestions.map((q, i) => ({
      ...q,
      questionOrder: i + 1
    }));
    setQuestions(reorderedQuestions);
    
    if (expandedQuestion === index) {
      setExpandedQuestion(null);
    } else if (expandedQuestion > index) {
      setExpandedQuestion(expandedQuestion - 1);
    }
  };

  // Update question
  const updateQuestion = (index, field, value) => {
    const updatedQuestions = [...questions];
    updatedQuestions[index] = { ...updatedQuestions[index], [field]: value };
    setQuestions(updatedQuestions);
  };

  // Add option to question
  const addOption = (questionIndex) => {
    const updatedQuestions = [...questions];
    const question = updatedQuestions[questionIndex];
    const newOption = {
      id: null,
      optionOrder: question.options.length + 1,
      optionText: '',
      optionScore: question.options.length + 1
    };
    question.options.push(newOption);
    setQuestions(updatedQuestions);
  };

  // Remove option from question
  const removeOption = (questionIndex, optionIndex) => {
    const updatedQuestions = [...questions];
    const question = updatedQuestions[questionIndex];
    
    if (question.options.length <= 2) {
      showMessage('Question must have at least 2 options', 'error');
      return;
    }
    
    question.options = question.options.filter((_, i) => i !== optionIndex);
    // Reorder remaining options
    question.options = question.options.map((option, i) => ({
      ...option,
      optionOrder: i + 1
    }));
    
    setQuestions(updatedQuestions);
  };

  // Update option
  const updateOption = (questionIndex, optionIndex, field, value) => {
    const updatedQuestions = [...questions];
    updatedQuestions[questionIndex].options[optionIndex] = {
      ...updatedQuestions[questionIndex].options[optionIndex],
      [field]: value
    };
    setQuestions(updatedQuestions);
  };

  // Move question up/down
  const moveQuestion = (index, direction) => {
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= questions.length) return;
    
    const updatedQuestions = [...questions];
    [updatedQuestions[index], updatedQuestions[newIndex]] = [updatedQuestions[newIndex], updatedQuestions[index]];
    
    // Update question orders
    updatedQuestions[index].questionOrder = index + 1;
    updatedQuestions[newIndex].questionOrder = newIndex + 1;
    
    setQuestions(updatedQuestions);
    setExpandedQuestion(newIndex);
  };

  // Duplicate question
  const duplicateQuestion = (index) => {
    const questionToDuplicate = questions[index];
    const duplicatedQuestion = {
      ...questionToDuplicate,
      id: null,
      questionOrder: questions.length + 1,
      questionText: questionToDuplicate.questionText + ' (Copy)',
      options: questionToDuplicate.options.map(opt => ({ ...opt, id: null }))
    };
    
    setQuestions([...questions, duplicatedQuestion]);
  };

  // Validate form
  const validateForm = () => {
    const errors = [];
    
    if (!formData.formName.trim()) {
      errors.push('Form name is required');
    }
    
    if (questions.length === 0) {
      errors.push('Form must have at least one question');
    }
    
    questions.forEach((question, qIndex) => {
      if (!question.questionText.trim()) {
        errors.push(`Question ${qIndex + 1} text is required`);
      }
      
      if (question.options.length < 2) {
        errors.push(`Question ${qIndex + 1} must have at least 2 options`);
      }
      
      question.options.forEach((option, oIndex) => {
        if (!option.optionText.trim()) {
          errors.push(`Question ${qIndex + 1}, Option ${oIndex + 1} text is required`);
        }
        
        if (!option.optionScore || option.optionScore < 1) {
          errors.push(`Question ${qIndex + 1}, Option ${oIndex + 1} must have a valid score (1 or higher)`);
        }
      });
    });
    
    return errors;
  };

  // Save form
  const saveForm = async () => {
    const validationErrors = validateForm();
    if (validationErrors.length > 0) {
      showMessage(validationErrors[0], 'error');
      return;
    }
    
    setSaving(true);
    try {
      const formPayload = {
        ...formData,
        questions: questions.map(q => ({
          ...q,
          options: q.options.map(opt => ({ ...opt }))
        }))
      };
      
      let response;
      if (editingForm) {
        response = await fetch(`${API_BASE_URL}/questionnaire-forms/${editingForm.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(formPayload)
        });
      } else {
        response = await fetch(`${API_BASE_URL}/questionnaire-forms`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(formPayload)
        });
      }
      
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      
      const result = await response.json();
      if (result.success) {
        showMessage(
          `Form ${editingForm ? 'updated' : 'created'} successfully!`, 
          'success'
        );
        
        // Close form builder after short delay
        setTimeout(() => {
          onClose && onClose(true);
        }, 1500);
      } else {
        throw new Error(result.message || 'Failed to save form');
      }
    } catch (error) {
      console.error('Error saving form:', error);
      showMessage('Failed to save form: ' + error.message, 'error');
    } finally {
      setSaving(false);
    }
  };

  // Show message
  const showMessage = (text, type = 'success') => {
    setMessage({ text, type });
    setTimeout(() => setMessage({ text: '', type: '' }), 5000);
  };

  // Calculate total possible score
  const calculateMaxScore = () => {
    return questions.reduce((total, question) => {
      const maxQuestionScore = Math.max(...question.options.map(opt => opt.optionScore || 0));
      return total + maxQuestionScore;
    }, 0);
  };

  const styles = {
    overlay: {
      position: 'fixed',
      inset: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.6)',
      zIndex: 50,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '1rem'
    },
    
    container: {
      backgroundColor: 'white',
      borderRadius: '12px',
      width: '100%',
      maxWidth: '1200px',
      height: '90vh',
      display: 'flex',
      flexDirection: 'column',
      boxShadow: '0 20px 40px rgba(0, 0, 0, 0.3)'
    },
    
    header: {
      padding: '1.5rem',
      borderBottom: '1px solid #e5e7eb',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      backgroundColor: '#f9fafb'
    },
    
    headerTitle: {
      fontSize: '1.5rem',
      fontWeight: '600',
      color: '#1a1a1a',
      margin: 0
    },
    
    headerActions: {
      display: 'flex',
      gap: '0.75rem',
      alignItems: 'center'
    },
    
    content: {
      flex: 1,
      display: 'flex',
      overflow: 'hidden'
    },
    
    sidebar: {
      width: '300px',
      borderRight: '1px solid #e5e7eb',
      backgroundColor: '#f9fafb',
      padding: '1.5rem',
      overflowY: 'auto'
    },
    
    main: {
      flex: 1,
      padding: '1.5rem',
      overflowY: 'auto'
    },
    
    tabs: {
      display: 'flex',
      borderBottom: '1px solid #e5e7eb',
      marginBottom: '1.5rem'
    },
    
    tab: {
      padding: '0.75rem 1rem',
      border: 'none',
      backgroundColor: 'transparent',
      cursor: 'pointer',
      fontSize: '0.875rem',
      fontWeight: '500',
      borderBottom: '2px solid transparent',
      transition: 'all 0.2s ease'
    },
    
    activeTab: {
      color: '#0ea5e9',
      borderBottomColor: '#0ea5e9'
    },
    
    formGroup: {
      marginBottom: '1rem'
    },
    
    label: {
      fontSize: '0.875rem',
      fontWeight: '500',
      color: '#374151',
      marginBottom: '0.5rem',
      display: 'block'
    },
    
    input: {
      width: '100%',
      padding: '0.75rem',
      border: '1px solid #d1d5db',
      borderRadius: '6px',
      fontSize: '1rem',
      transition: 'border-color 0.2s ease',
      boxSizing: 'border-box'
    },
    
    textarea: {
      width: '100%',
      padding: '0.75rem',
      border: '1px solid #d1d5db',
      borderRadius: '6px',
      fontSize: '1rem',
      minHeight: '80px',
      resize: 'vertical',
      transition: 'border-color 0.2s ease',
      boxSizing: 'border-box'
    },
    
    select: {
      width: '100%',
      padding: '0.75rem',
      border: '1px solid #d1d5db',
      borderRadius: '6px',
      fontSize: '1rem',
      backgroundColor: 'white',
      cursor: 'pointer'
    },
    
    checkbox: {
      marginRight: '0.5rem'
    },
    
    button: {
      padding: '0.75rem 1.5rem',
      border: 'none',
      borderRadius: '8px',
      fontSize: '1rem',
      fontWeight: '500',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      transition: 'all 0.2s ease'
    },
    
    primaryButton: {
      backgroundColor: '#0ea5e9',
      color: 'white'
    },
    
    secondaryButton: {
      backgroundColor: '#6b7280',
      color: 'white'
    },
    
    dangerButton: {
      backgroundColor: '#ef4444',
      color: 'white'
    },
    
    successButton: {
      backgroundColor: '#10b981',
      color: 'white'
    },
    
    questionCard: {
      border: '1px solid #e5e7eb',
      borderRadius: '8px',
      marginBottom: '1rem',
      backgroundColor: 'white',
      overflow: 'hidden'
    },
    
    questionHeader: {
      padding: '1rem',
      backgroundColor: '#f9fafb',
      borderBottom: '1px solid #e5e7eb',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      cursor: 'pointer'
    },
    
    questionContent: {
      padding: '1rem'
    },
    
    optionItem: {
      border: '1px solid #e5e7eb',
      borderRadius: '6px',
      padding: '0.75rem',
      marginBottom: '0.75rem',
      backgroundColor: '#fafbfc'
    },
    
    optionHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '0.5rem'
    },
    
    message: {
      position: 'fixed',
      top: '1rem',
      right: '1rem',
      zIndex: 1000,
      padding: '1rem 1.5rem',
      borderRadius: '8px',
      fontWeight: '500',
      boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      maxWidth: '400px'
    },
    
    successMessage: {
      backgroundColor: '#d1fae5',
      color: '#065f46',
      border: '1px solid #a7f3d0'
    },
    
    errorMessage: {
      backgroundColor: '#fee2e2',
      color: '#991b1b',
      border: '1px solid #fca5a5'
    }
  };

  return (
    <>
      <style>{`
        .form-input:focus, .form-textarea:focus, .form-select:focus {
          outline: none;
          border-color: #0ea5e9;
          box-shadow: 0 0 0 3px rgba(14, 165, 233, 0.1);
        }
        
        .button:hover {
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        }
        
        .question-card:hover {
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
        }
        
        @media (max-width: 768px) {
          .content {
            flex-direction: column !important;
          }
          
          .sidebar {
            width: 100% !important;
            border-right: none !important;
            border-bottom: 1px solid #e5e7eb !important;
          }
        }
      `}</style>

      <div style={styles.overlay}>
        {/* Message Notification */}
        {message.text && (
          <div style={{
            ...styles.message,
            ...(message.type === 'success' ? styles.successMessage : styles.errorMessage)
          }}>
            {message.type === 'success' ? (
              <CheckCircle size={20} />
            ) : (
              <AlertCircle size={20} />
            )}
            <span>{message.text}</span>
            <button
              onClick={() => setMessage({ text: '', type: '' })}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                marginLeft: '0.5rem'
              }}
            >
              <X size={16} />
            </button>
          </div>
        )}

        <div style={styles.container}>
          {/* Header */}
          <div style={styles.header}>
            <h2 style={styles.headerTitle}>
              {editingForm ? 'Edit Form' : 'Create New Form'}
            </h2>
            
            <div style={styles.headerActions}>
              <span style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                Max Score: {calculateMaxScore()}
              </span>
              
              <button
                onClick={() => setActiveTab('preview')}
                style={{
                  ...styles.button,
                  padding: '0.5rem 1rem',
                  backgroundColor: '#8b5cf6',
                  color: 'white'
                }}
                className="button"
              >
                <Eye size={16} />
                Preview
              </button>
              
              <button
                onClick={saveForm}
                disabled={saving}
                style={{
                  ...styles.button,
                  ...styles.successButton,
                  opacity: saving ? 0.7 : 1,
                  cursor: saving ? 'not-allowed' : 'pointer'
                }}
                className="button"
              >
                {saving ? (
                  <>
                    <div style={{
                      width: '16px',
                      height: '16px',
                      border: '2px solid transparent',
                      borderTop: '2px solid white',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite'
                    }} />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save size={16} />
                    Save Form
                  </>
                )}
              </button>
              
              <button
                onClick={() => onClose && onClose(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '0.5rem'
                }}
              >
                <X size={24} />
              </button>
            </div>
          </div>

          <div style={styles.content} className="content">
            {/* Sidebar */}
            <div style={styles.sidebar} className="sidebar">
              <div style={styles.formGroup}>
                <label style={styles.label}>Form Name *</label>
                <input
                  type="text"
                  value={formData.formName}
                  onChange={(e) => setFormData(prev => ({ ...prev, formName: e.target.value }))}
                  style={styles.input}
                  className="form-input"
                  placeholder="Enter form name..."
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  style={styles.textarea}
                  className="form-textarea"
                  placeholder="Describe your form..."
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>
                  <input
                    type="checkbox"
                    checked={formData.isActive}
                    onChange={(e) => setFormData(prev => ({ ...prev, isActive: e.target.checked }))}
                    style={styles.checkbox}
                  />
                  Active Form
                </label>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>
                  <input
                    type="checkbox"
                    checked={formData.isDefault}
                    onChange={(e) => setFormData(prev => ({ ...prev, isDefault: e.target.checked }))}
                    style={styles.checkbox}
                  />
                  Set as Default Form
                </label>
              </div>

              <div style={{ marginTop: '2rem', padding: '1rem', backgroundColor: '#eff6ff', borderRadius: '8px' }}>
                <h4 style={{ margin: '0 0 0.5rem', fontSize: '0.875rem', fontWeight: '600', color: '#1e40af' }}>
                  Form Statistics
                </h4>
                <div style={{ fontSize: '0.75rem', color: '#1e40af' }}>
                  <div>Questions: {questions.length}</div>
                  <div>Total Options: {questions.reduce((sum, q) => sum + q.options.length, 0)}</div>
                  <div>Max Possible Score: {calculateMaxScore()}</div>
                </div>
              </div>
            </div>

            {/* Main Content */}
            <div style={styles.main}>
              {/* Tabs */}
              <div style={styles.tabs}>
                <button
                  onClick={() => setActiveTab('builder')}
                  style={{
                    ...styles.tab,
                    ...(activeTab === 'builder' ? styles.activeTab : {})
                  }}
                >
                  <Settings size={16} style={{ marginRight: '0.5rem' }} />
                  Form Builder
                </button>
                <button
                  onClick={() => setActiveTab('preview')}
                  style={{
                    ...styles.tab,
                    ...(activeTab === 'preview' ? styles.activeTab : {})
                  }}
                >
                  <Eye size={16} style={{ marginRight: '0.5rem' }} />
                  Preview
                </button>
              </div>

              {/* Builder Tab */}
              {activeTab === 'builder' && (
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                    <h3 style={{ margin: 0, fontSize: '1.25rem', fontWeight: '600' }}>
                      Questions ({questions.length})
                    </h3>
                    <button
                      onClick={addQuestion}
                      style={{ ...styles.button, ...styles.primaryButton }}
                      className="button"
                    >
                      <Plus size={16} />
                      Add Question
                    </button>
                  </div>

                  {questions.map((question, questionIndex) => (
                    <div 
                      key={questionIndex} 
                      style={styles.questionCard}
                      className="question-card"
                    >
                      <div 
                        style={styles.questionHeader}
                        onClick={() => setExpandedQuestion(
                          expandedQuestion === questionIndex ? null : questionIndex
                        )}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                          <GripVertical size={16} color="#6b7280" />
                          <span style={{ fontWeight: '600' }}>
                            Question {questionIndex + 1}
                          </span>
                          <span style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                            {question.questionText || 'Untitled Question'}
                          </span>
                        </div>
                        
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              moveQuestion(questionIndex, 'up');
                            }}
                            style={{
                              background: 'none',
                              border: 'none',
                              cursor: 'pointer',
                              padding: '0.25rem',
                              color: '#6b7280'
                            }}
                            disabled={questionIndex === 0}
                            title="Move Up"
                          >
                            <ArrowUp size={16} />
                          </button>
                          
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              moveQuestion(questionIndex, 'down');
                            }}
                            style={{
                              background: 'none',
                              border: 'none',
                              cursor: 'pointer',
                              padding: '0.25rem',
                              color: '#6b7280'
                            }}
                            disabled={questionIndex === questions.length - 1}
                            title="Move Down"
                          >
                            <ArrowDown size={16} />
                          </button>
                          
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              duplicateQuestion(questionIndex);
                            }}
                            style={{
                              background: 'none',
                              border: 'none',
                              cursor: 'pointer',
                              padding: '0.25rem',
                              color: '#0ea5e9'
                            }}
                            title="Duplicate"
                          >
                            <Copy size={16} />
                          </button>
                          
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              removeQuestion(questionIndex);
                            }}
                            style={{
                              background: 'none',
                              border: 'none',
                              cursor: 'pointer',
                              padding: '0.25rem',
                              color: '#ef4444'
                            }}
                            title="Delete"
                          >
                            <Trash2 size={16} />
                          </button>
                          
                          {expandedQuestion === questionIndex ? (
                            <ChevronUp size={16} color="#6b7280" />
                          ) : (
                            <ChevronDown size={16} color="#6b7280" />
                          )}
                        </div>
                      </div>

                      {expandedQuestion === questionIndex && (
                        <div style={styles.questionContent}>
                          <div style={styles.formGroup}>
                            <label style={styles.label}>Question Text *</label>
                            <textarea
                              value={question.questionText}
                              onChange={(e) => updateQuestion(questionIndex, 'questionText', e.target.value)}
                              style={styles.textarea}
                              className="form-textarea"
                              placeholder="Enter your question..."
                            />
                          </div>

                          <div style={styles.formGroup}>
                            <label style={styles.label}>Question Type</label>
                            <select
                              value={question.questionType}
                              onChange={(e) => updateQuestion(questionIndex, 'questionType', e.target.value)}
                              style={styles.select}
                              className="form-select"
                            >
                              <option value="SINGLE_CHOICE">Single Choice</option>
                              <option value="MULTIPLE_CHOICE">Multiple Choice</option>
                              <option value="TEXT">Text Input</option>
                            </select>
                          </div>

                          {question.questionType !== 'TEXT' && (
                            <div>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                                <label style={styles.label}>Answer Options</label>
                                <button
                                  onClick={() => addOption(questionIndex)}
                                  style={{
                                    ...styles.button,
                                    padding: '0.5rem 0.75rem',
                                    fontSize: '0.875rem',
                                    backgroundColor: '#10b981',
                                    color: 'white'
                                  }}
                                  className="button"
                                >
                                  <Plus size={14} />
                                  Add Option
                                </button>
                              </div>

                              {question.options.map((option, optionIndex) => (
                                <div key={optionIndex} style={styles.optionItem}>
                                  <div style={styles.optionHeader}>
                                    <span style={{ fontSize: '0.875rem', fontWeight: '500', color: '#374151' }}>
                                      Option {optionIndex + 1}
                                    </span>
                                    <button
                                      onClick={() => removeOption(questionIndex, optionIndex)}
                                      style={{
                                        background: 'none',
                                        border: 'none',
                                        cursor: 'pointer',
                                        padding: '0.25rem',
                                        color: '#ef4444'
                                      }}
                                      title="Remove Option"
                                    >
                                      <Trash2 size={14} />
                                    </button>
                                  </div>
                                  
                                  <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '0.75rem', alignItems: 'center' }}>
                                    <input
                                      type="text"
                                      value={option.optionText}
                                      onChange={(e) => updateOption(questionIndex, optionIndex, 'optionText', e.target.value)}
                                      style={styles.input}
                                      className="form-input"
                                      placeholder="Enter option text..."
                                    />
                                    
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                      <label style={{ fontSize: '0.75rem', color: '#6b7280' }}>Score:</label>
                                      <input
                                        type="number"
                                        value={option.optionScore}
                                        onChange={(e) => updateOption(questionIndex, optionIndex, 'optionScore', parseInt(e.target.value) || 1)}
                                        style={{ ...styles.input, width: '80px' }}
                                        className="form-input"
                                        min="1"
                                        max="10"
                                      />
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}

                  {questions.length === 0 && (
                    <div style={{
                      textAlign: 'center',
                      padding: '3rem',
                      color: '#6b7280',
                      border: '2px dashed #d1d5db',
                      borderRadius: '8px'
                    }}>
                      <FileText size={48} style={{ margin: '0 auto 1rem', opacity: 0.5 }} />
                      <h3 style={{ fontSize: '1.25rem', marginBottom: '0.5rem', color: '#1a1a1a' }}>
                        No questions yet
                      </h3>
                      <p style={{ marginBottom: '1.5rem' }}>
                        Add your first question to get started building your form.
                      </p>
                      <button
                        onClick={addQuestion}
                        style={{ ...styles.button, ...styles.primaryButton }}
                        className="button"
                      >
                        <Plus size={16} />
                        Add First Question
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* Preview Tab */}
              {activeTab === 'preview' && (
                <div>
                  <div style={{
                    marginBottom: '2rem',
                    padding: '1rem',
                    backgroundColor: '#eff6ff',
                    borderRadius: '8px',
                    border: '1px solid #bfdbfe'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div>
                        <h3 style={{ margin: '0 0 0.25rem', fontSize: '1.25rem', fontWeight: '600', color: '#1e40af' }}>
                          Form Preview: {formData.formName || 'Untitled Form'}
                        </h3>
                        <p style={{ margin: 0, fontSize: '0.875rem', color: '#1e40af' }}>
                          {formData.description || 'No description provided'}
                        </p>
                      </div>
                      <div style={{ fontSize: '0.875rem', color: '#1e40af' }}>
                        <div>Questions: {questions.length}</div>
                        <div>Max Score: {calculateMaxScore()}</div>
                      </div>
                    </div>
                  </div>

                  {questions.length === 0 ? (
                    <div style={{
                      textAlign: 'center',
                      padding: '3rem',
                      color: '#6b7280',
                      border: '2px dashed #d1d5db',
                      borderRadius: '8px'
                    }}>
                      <Eye size={48} style={{ margin: '0 auto 1rem', opacity: 0.5 }} />
                      <h3 style={{ fontSize: '1.25rem', marginBottom: '0.5rem', color: '#1a1a1a' }}>
                        No questions to preview
                      </h3>
                      <p>Add some questions in the Form Builder tab to see the preview.</p>
                    </div>
                  ) : (
                    <div>
                      {questions.map((question, index) => (
                        <div 
                          key={index}
                          style={{
                            marginBottom: '2rem',
                            padding: '1.5rem',
                            border: '1px solid #e5e7eb',
                            borderRadius: '8px',
                            backgroundColor: 'white'
                          }}
                        >
                          <h4 style={{
                            margin: '0 0 1rem',
                            fontSize: '1.125rem',
                            fontWeight: '600',
                            color: '#1a1a1a'
                          }}>
                            {index + 1}. {question.questionText || 'Untitled Question'}
                          </h4>

                          {question.questionType === 'TEXT' ? (
                            <textarea
                              placeholder="User will type their answer here..."
                              style={{
                                ...styles.textarea,
                                backgroundColor: '#f9fafb',
                                cursor: 'not-allowed'
                              }}
                              disabled
                            />
                          ) : (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                              {question.options.map((option, optionIndex) => (
                                <label
                                  key={optionIndex}
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.75rem',
                                    padding: '0.75rem',
                                    border: '1px solid #d1d5db',
                                    borderRadius: '6px',
                                    cursor: 'pointer',
                                    backgroundColor: '#fafbfc',
                                    transition: 'all 0.2s ease'
                                  }}
                                >
                                  <input
                                    type={question.questionType === 'SINGLE_CHOICE' ? 'radio' : 'checkbox'}
                                    name={`preview-question-${index}`}
                                    style={{ cursor: 'pointer' }}
                                    disabled
                                  />
                                  <span style={{ flex: 1 }}>
                                    {option.optionText || `Option ${optionIndex + 1}`}
                                  </span>
                                  <span style={{
                                    fontSize: '0.75rem',
                                    color: '#6b7280',
                                    backgroundColor: '#e5e7eb',
                                    padding: '0.25rem 0.5rem',
                                    borderRadius: '4px'
                                  }}>
                                    Score: {option.optionScore}
                                  </span>
                                </label>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}

                      <div style={{
                        marginTop: '2rem',
                        padding: '1.5rem',
                        backgroundColor: '#f9fafb',
                        borderRadius: '8px',
                        border: '1px solid #e5e7eb'
                      }}>
                        <h4 style={{ margin: '0 0 1rem', fontSize: '1.125rem', fontWeight: '600' }}>
                          Risk Profile Calculation
                        </h4>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '1rem' }}>
                          <div>
                            <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>Score Range</div>
                            <div style={{ fontSize: '1.125rem', fontWeight: '600', color: '#1a1a1a' }}>
                              1 - {calculateMaxScore()}
                            </div>
                          </div>
                          <div>
                            <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>Risk Profiles</div>
                            <div style={{ fontSize: '1.125rem', fontWeight: '600', color: '#1a1a1a' }}>
                              6 Categories
                            </div>
                          </div>
                          <div>
                            <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>Questions</div>
                            <div style={{ fontSize: '1.125rem', fontWeight: '600', color: '#1a1a1a' }}>
                              {questions.length}
                            </div>
                          </div>
                        </div>
                      </div>

                      <div style={{
                        marginTop: '1rem',
                        textAlign: 'center',
                        padding: '1.5rem',
                        backgroundColor: '#f0fdf4',
                        borderRadius: '8px',
                        border: '1px solid #a7f3d0'
                      }}>
                        <button
                          style={{
                            ...styles.button,
                            ...styles.successButton,
                            fontSize: '1.125rem',
                            padding: '1rem 2rem'
                          }}
                          disabled
                        >
                          <Play size={18} />
                          Submit Risk Profile Assessment
                        </button>
                        <p style={{
                          margin: '0.5rem 0 0',
                          fontSize: '0.875rem',
                          color: '#16a34a'
                        }}>
                          This is how the submit button will appear to users
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

// Updated Admin Dashboard with Form Builder Integration
const RiskProfileAdminDashboardWithFormBuilder = () => {
  const [showFormBuilder, setShowFormBuilder] = useState(false);
  const [editingForm, setEditingForm] = useState(null);

  const handleFormBuilderClose = (shouldRefresh) => {
    setShowFormBuilder(false);
    setEditingForm(null);
    if (shouldRefresh) {
      // Refresh forms list or dashboard data
      window.location.reload(); // Simple refresh - could be improved with state management
    }
  };

  const openFormBuilder = (form = null) => {
    setEditingForm(form);
    setShowFormBuilder(true);
  };

  return (
    <div>
      {/* Your existing admin dashboard component */}
      {/* Add buttons to open form builder */}
      <button
        onClick={() => openFormBuilder()}
        style={{
          padding: '0.75rem 1.5rem',
          backgroundColor: '#0ea5e9',
          color: 'white',
          border: 'none',
          borderRadius: '8px',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem'
        }}
      >
        <Plus size={16} />
        Create New Form
      </button>

      {/* Form Builder Modal */}
      {showFormBuilder && (
        <RiskProfileFormBuilder
          onClose={handleFormBuilderClose}
          editingForm={editingForm}
        />
      )}
    </div>
  );
};

export default RiskProfileCreatePage;